import { useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useWorkspaces } from '../providers/WorkspaceProvider';
import { apiClient } from '../services/apiClient';
import { ThemeToggle } from './ThemeToggle';
import { GenerateOptionsModal } from './GenerateOptionsModal';
import {
  SidebarWrapper,
  LogoArea,
  Logo,
  PlanBar,
  CreditBar,
  Nav,
  CollapseToggle,
  NavSection,
  NavSectionLabel,
  NavSectionToggle,
  Chevron,
  StyledNavLink,
  NavIcon,
  Label,
  BetaBadge,
  WorkspaceNameBanner,
  WsNameText,
  WsDot,
  WsIcon,
  WorkspaceList,
  WsItem,
  WsDeleteBtn,
  CreateWorkspaceButton,
  NavButton,
  Spacer,
  Divider,
  BottomTool,
  HelpLink,
  LogoutButton,
} from './sidebar/styled';

// Clave de persistencia del estado colapsado del sidebar (solo desktop).
const COLLAPSE_KEY = 'smm_sidebar_collapsed';

const WORKSPACE_COLORS = ['#3B82F6', '#8B5CF6', '#EC4899', '#F59E0B', '#10B981', '#06B6D4', '#F43F5E', '#6366F1'];

function getWsColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return WORKSPACE_COLORS[Math.abs(hash) % WORKSPACE_COLORS.length];
}

export function Sidebar({ isOpen }: { isOpen: boolean }) {
  const { user, logout, subscriptionActive, canUse, credits } = useAuth();
  const { workspaces, removeWorkspace } = useWorkspaces();
  const [workspacesOpen, setWorkspacesOpen] = useState(false);
  const [genModalOpen, setGenModalOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem(COLLAPSE_KEY) === '1'; } catch { return false; }
  });
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const toggleCollapsed = () => {
    setCollapsed((v) => {
      const next = !v;
      try { localStorage.setItem(COLLAPSE_KEY, next ? '1' : '0'); } catch { /* noop */ }
      return next;
    });
  };

  // Borrar workspace (solo el dueño; el server lo valida igual). Confirmación
  // explícita porque es irreversible y arrastra posts/assets/templates.
  const handleDeleteWorkspace = async (e: React.MouseEvent, ws: { id: string; name: string }) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm(`Delete "${ws.name}"? This permanently removes all its posts, assets and templates.`)) return;
    try {
      await apiClient.delete(`/api/workspaces/${ws.id}`);
      removeWorkspace(ws.id);
      if (workspaceId === ws.id) navigate('/dashboard');
    } catch {
      alert('Failed to delete workspace. Please try again.');
    }
  };

  const isSuperAdmin = user?.role === 'super_admin';
  const isModOrAdmin = user?.role === 'mod' || user?.role === 'admin' || isSuperAdmin;
  const isInWorkspace = location.pathname.includes('/workspace/') && workspaceId;
  const currentWorkspace = workspaces.find((w) => w.id === workspaceId);
  // Workspace destino de los accesos directos del top (Templates/Styles/
  // Collaborators): el actual si estás dentro de uno, si no el más reciente.
  // Si no hay ninguno, esos links se ocultan (no hay a dónde apuntar).
  const targetWs = workspaceId || workspaces[0]?.id;

  return (
    <SidebarWrapper $isOpen={isOpen} $collapsed={collapsed} className={collapsed ? 'is-collapsed' : undefined}>
      <LogoArea>
        <Logo>
          <video
            src="/loaders/agency-logo.mp4"
            poster="/MainLogo1.png"
            autoPlay
            muted
            loop
            playsInline
            aria-label="AgencySMM"
          />
        </Logo>
        {subscriptionActive && (
          <PlanBar>
            <span>&#x2713;</span>
            <Label>Active plan</Label>
          </PlanBar>
        )}
        {!subscriptionActive && canUse && credits !== null && (
          <CreditBar type="button" onClick={() => navigate('/subscription')} title="Subscribe for unlimited use">
            <span>&#x26A1;</span>
            <Label>{credits} {credits === 1 ? 'credit' : 'credits'} · trial</Label>
          </CreditBar>
        )}
      </LogoArea>

      {/* Workspace name banner — always visible when in a workspace (no en rail) */}
      {isInWorkspace && currentWorkspace && !collapsed && (
        <WorkspaceNameBanner>
          <WsDot $color={getWsColor(currentWorkspace.name)} />
          <WsNameText>{currentWorkspace.name}</WsNameText>
        </WorkspaceNameBanner>
      )}

      <Nav>
        {/* Toggle de colapso (desktop) */}
        <CollapseToggle
          type="button"
          onClick={toggleCollapsed}
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
          aria-expanded={!collapsed}
          title={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? '»' : '«'}
        </CollapseToggle>

        {/* Quick links */}
        <NavSection>
          <StyledNavLink to="/dashboard" title="Dashboard">
            <NavIcon>📊</NavIcon> <Label>Dashboard</Label>
          </StyledNavLink>
          <StyledNavLink to="/quick-gen" title="Quick Gen">
            <NavIcon>⚡</NavIcon> <Label>Quick Gen</Label> <BetaBadge>Beta</BetaBadge>
          </StyledNavLink>
          {/* Accesos directos siempre visibles (apuntan al workspace activo o al
              más reciente). Ver targetWs. */}
          {targetWs && (
            <>
              <StyledNavLink to={`/workspace/${targetWs}/templates`} title="Templates">
                <NavIcon>🎨</NavIcon> <Label>Templates</Label>
              </StyledNavLink>
              <StyledNavLink to={`/workspace/${targetWs}/styles`} title="Styles">
                <NavIcon>🪄</NavIcon> <Label>Styles</Label>
              </StyledNavLink>
              <StyledNavLink to={`/workspace/${targetWs}/team`} title="Collaborators">
                <NavIcon>👥</NavIcon> <Label>Collaborators</Label>
              </StyledNavLink>
            </>
          )}
          <StyledNavLink to="/subscription" title="Subscription">
            <NavIcon>💳</NavIcon> <Label>Subscription</Label>
          </StyledNavLink>
        </NavSection>

        {/* Workspace pages — shown when inside a workspace */}
        {isInWorkspace && (
          <NavSection>
            <NavSectionLabel>Workspace</NavSectionLabel>
            <StyledNavLink to={`/workspace/${workspaceId}`} end title="Home">
              <NavIcon>🏠</NavIcon> <Label>Home</Label>
            </StyledNavLink>
            <NavButton type="button" onClick={() => setGenModalOpen(true)} title="Generate">
              <NavIcon>✨</NavIcon> <Label>Generate</Label>
            </NavButton>
            <StyledNavLink to={`/workspace/${workspaceId}/brand`} title="Brand">
              <NavIcon>🎨</NavIcon> <Label>Brand</Label>
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/photo-stylize`} title="Image to Image">
              <NavIcon>📸</NavIcon> <Label>Image to Image</Label> <BetaBadge>Beta</BetaBadge>
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/gallery`} title="Gallery">
              <NavIcon>🖼️</NavIcon> <Label>Gallery</Label>
            </StyledNavLink>
            <StyledNavLink to={`/workspace/${workspaceId}/ai-visibility`} title="AI Visibility">
              <NavIcon>📡</NavIcon> <Label>AI Visibility</Label> <BetaBadge>Beta</BetaBadge>
            </StyledNavLink>
          </NavSection>
        )}

        {/* Admin section */}
        {isModOrAdmin && (
          <NavSection>
            <NavSectionLabel>Admin</NavSectionLabel>
            <StyledNavLink to="/mod" title="Prompts">
              <NavIcon>📝</NavIcon> <Label>Prompts</Label>
            </StyledNavLink>
            <StyledNavLink to="/admin/analytics" title="Usage">
              <NavIcon>📊</NavIcon> <Label>Usage</Label>
            </StyledNavLink>
            <StyledNavLink to="/admin/llm-trace" title="LLM Trace">
              <NavIcon>🔬</NavIcon> <Label>LLM Trace</Label>
            </StyledNavLink>
            {user?.isAdmin && (
              <>
                <StyledNavLink to="/admin/templates" title="Templates">
                  <NavIcon>🎨</NavIcon> <Label>Templates</Label>
                </StyledNavLink>
                <StyledNavLink to="/admin/styles" title="Styles">
                  <NavIcon>🪄</NavIcon> <Label>Styles</Label>
                </StyledNavLink>
                <StyledNavLink to="/admin" title="Admin Panel">
                  <NavIcon>⚙️</NavIcon> <Label>Admin Panel</Label>
                </StyledNavLink>
              </>
            )}
          </NavSection>
        )}

        {/* Workspaces list — oculto en el rail colapsado */}
        {!collapsed && (
          <NavSection>
            <NavSectionToggle onClick={() => setWorkspacesOpen((v) => !v)}>
              All Workspaces
              <Chevron $open={workspacesOpen}>▶</Chevron>
            </NavSectionToggle>
            {workspacesOpen && (
              <WorkspaceList>
                {workspaces.map((workspace) => (
                  <WsItem key={workspace.id}>
                    <StyledNavLink
                      to={`/workspace/${workspace.id}`}
                      className={workspaceId === workspace.id ? 'active' : ''}
                    >
                      {workspace.logoUrl ? (
                        <WsIcon src={workspace.logoUrl} alt="" />
                      ) : (
                        <WsDot $color={getWsColor(workspace.name)} />
                      )}
                      {workspace.name}
                      {workspace.owned !== undefined && !workspace.owned && (
                        <BetaBadge title="Shared with you">Shared</BetaBadge>
                      )}
                    </StyledNavLink>
                    {workspace.owned !== false && (
                      <WsDeleteBtn
                        data-ws-del
                        type="button"
                        title={`Delete ${workspace.name}`}
                        aria-label={`Delete ${workspace.name}`}
                        onClick={(e) => handleDeleteWorkspace(e, workspace)}
                      >
                        ×
                      </WsDeleteBtn>
                    )}
                  </WsItem>
                ))}
                <li>
                  <CreateWorkspaceButton onClick={() => navigate('/dashboard?create=true')}>
                    + New Workspace
                  </CreateWorkspaceButton>
                </li>
              </WorkspaceList>
            )}
          </NavSection>
        )}

        <Spacer />
        <Divider />
        <BottomTool>
          <ThemeToggle />
        </BottomTool>
        <HelpLink href="/guia.html" target="_blank" rel="noopener noreferrer" title="Help · User Guide">
          <NavIcon>❓</NavIcon> <Label>Help · User Guide</Label>
        </HelpLink>
        <LogoutButton onClick={logout} title="Logout">
          <NavIcon>🚪</NavIcon> <Label>Logout</Label>
        </LogoutButton>
      </Nav>

      {workspaceId && (
        <GenerateOptionsModal
          workspaceId={workspaceId}
          open={genModalOpen}
          onClose={() => setGenModalOpen(false)}
        />
      )}
    </SidebarWrapper>
  );
}
